var _excluded = ["id", "input", "meta", "className", "errorComponent", "labelComponent", "children", "hideErrorLabel", "label"];
function _typeof(obj) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof(obj); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import InputError from './input-error';
import InputLabel from './input-label';
import { hasInputError } from '../helpers';

/**
 *
 * A fieldset wrapper for redux-form controlled inputs. This wrapper adds a label component (defaults to {@link InputLabel})
 * above the wrapped component and an error component below (defaults to {@link InputError}). Additionally, it adds the class `"error"`
 * to the fieldset if the input is touched and invalid.
 *
 * In order to populate the `InputLabel` and `InputError` correctly, you should pass all the props of the corresponding input
 * to this component. To prevent label-specific props from being passed to the input itself,
 * use the {@link omitLabelProps} helper.
 *
 * @name LabeledField
 * @type Function
 * @param {Boolean} [hideErrorLabel] - A boolean determining whether to hide the error label on input error (optional, default `false`)
 * @param {Function} [labelComponent=InputLabel] - A custom label component for the input
 * @param {Function} [errorComponent=InputError] - A custom error component for the input
 *
 * @example
 *
 * // A custom input to use with redux-form
 *
 * function LabeledPhoneInput (props) {
 *   const {
 *      input: { name, value, onBlur, onChange },
 *      ...rest,
 *   } = omitLabelProps(props)
 *   return (
 *      <LabeledField { ...props }>
 *        <input {...{
 *          type: 'phone',
 *          name,
 *          value,
 *          onBlur,
 *          onChange,
 *          ...rest,
 *        }}
 *     </LabeledField>
 *   )
 * }
 *
 * // A custom label to pass in as a label component (using <InputLabel /> and redux-form)
 *
 * import LabeledPhoneInput from './LabeledPhoneInput'
 * import { InputLabel } from 'lp-components'
 * import { Field } from 'redux-form'
 *
 * function CustomLabelComponent ({ onClickLabel, ...rest }) {
 *  return (
 *    <InputLabel { ...rest }>
 *      I agree to the <button onClick={ onClickLabel }>Terms and Conditions</button>
 *    </InputLabel>
 *  )
 * }
 *
 * <Field
 *   name="phoneNumber"
 *   component={ LabeledPhoneInput }
 *   onClickLabel={ () => 'bar' }
 *   labelComponent={ CustomLabelComponent }
 * />
 *
 */

var propTypes = _objectSpread(_objectSpread(_objectSpread({}, InputLabel.propTypes), InputError.propTypes), {}, {
  children: PropTypes.node,
  hideErrorLabel: PropTypes.bool
});
var defaultProps = {
  hideErrorLabel: false
};
function LabeledField(_ref) {
  var id = _ref.id,
    input = _ref.input,
    meta = _ref.meta,
    className = _ref.className,
    _ref$errorComponent = _ref.errorComponent,
    ErrorComponent = _ref$errorComponent === void 0 ? InputError : _ref$errorComponent,
    _ref$labelComponent = _ref.labelComponent,
    LabelComponent = _ref$labelComponent === void 0 ? InputLabel : _ref$labelComponent,
    children = _ref.children,
    hideErrorLabel = _ref.hideErrorLabel,
    label = _ref.label,
    rest = _objectWithoutProperties(_ref, _excluded);
  var name = input.name;
  var touched = meta.touched,
    invalid = meta.invalid;
  return /*#__PURE__*/React.createElement("fieldset", {
    className: classnames(className, {
      error: hasInputError({
        touched: touched,
        invalid: invalid
      }),
      disabled: rest.disabled
    })
  }, /*#__PURE__*/React.createElement(LabelComponent, _objectSpread({
    name: name,
    id: id,
    label: label
  }, rest)), children, !hideErrorLabel && /*#__PURE__*/React.createElement(ErrorComponent, _objectSpread(_objectSpread(_objectSpread({}, input), meta), rest)));
}
LabeledField.propTypes = propTypes;
LabeledField.defaultProps = defaultProps;
export default LabeledField;